import React, { useState } from 'react';
import { X, Save, Building } from 'lucide-react';
import Select from 'react-select';
import { useHealthZones, useDoctors } from '../../hooks/useApi';

const HospitalAddModal = ({ isOpen, onClose, onSave, loading }) => {
  const { data: healthZones = [], loading: healthZonesLoading } = useHealthZones();
  const { data: doctors = [], loading: doctorsLoading } = useDoctors();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    healthZoneId: null,
    province: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    directorId: null,
    category: 'Hôpital général',
    level: 'Secondaire',
    beds: 0,
    description: '',
    status: 'Opérationnel'
  });

  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [errors, setErrors] = useState({});

  const healthZoneOptions = healthZones.map(zone => ({
    value: zone.id,
    label: `${zone.name} (${zone.code}) - ${zone.province}`
  }));

  const directorOptions = doctors
    .filter(doctor => doctor.user)
    .map(doctor => ({
      value: doctor.id,
      label: `Dr. ${doctor.user.name} - ${doctor.speciality || 'Non spécifié'}`
    }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleZoneChange = (selectedOption) => {
    setSelectedZone(selectedOption);
    setFormData(prev => ({ ...prev, healthZoneId: selectedOption ? selectedOption.value : null }));
    if (errors.healthZoneId) {
      setErrors(prev => ({ ...prev, healthZoneId: '' }));
    }
  };

  const handleDirectorChange = (selectedOption) => {
    setSelectedDirector(selectedOption);
    setFormData(prev => ({ ...prev, directorId: selectedOption ? selectedOption.value : null }));
    if (errors.directorId) {
      setErrors(prev => ({ ...prev, directorId: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.code.trim()) newErrors.code = 'Le code est requis';
    if (!formData.healthZoneId) newErrors.healthZoneId = 'La zone de santé est requise';
    if (!formData.province.trim()) newErrors.province = 'La province est requise';
    if (!formData.city.trim()) newErrors.city = 'La ville est requise';
    if (formData.email && !formData.email.includes('@')) newErrors.email = 'Email invalide';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Ajouter un Hôpital</h2>
              <p className="text-gray-600">Créer un nouvel établissement de santé</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" disabled={loading}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations de base */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de base</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'hôpital *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: Hôpital Général de Kinshasa"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Code *</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: HGK"
                />
                {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Province *</label>
                <input
                  type="text"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.province ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: Kinshasa"
                />
                {errors.province && <p className="text-red-500 text-sm mt-1">{errors.province}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ville *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: Kinshasa"
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
              </div>
            </div>
          </div>

          {/* Zone de santé et Directeur */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Zone de santé et Personnel</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zone de santé *</label>
                <Select
                  value={selectedZone}
                  onChange={handleZoneChange}
                  options={healthZoneOptions}
                  isLoading={healthZonesLoading}
                  placeholder="Sélectionner une zone de santé..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.healthZoneId && <p className="text-red-500 text-sm mt-1">{errors.healthZoneId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Directeur (optionnel)</label>
                <Select
                  value={selectedDirector}
                  onChange={handleDirectorChange}
                  options={directorOptions}
                  isLoading={doctorsLoading}
                  isClearable
                  placeholder="Sélectionner un directeur..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </div>

          {/* Catégorie et Statut */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Classification</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Hôpital général">Hôpital général</option>
                  <option value="Hôpital universitaire">Hôpital universitaire</option>
                  <option value="Hôpital psychiatrique">Hôpital psychiatrique</option>
                  <option value="Hôpital spécialisé">Hôpital spécialisé</option>
                  <option value="Centre hospitalier">Centre hospitalier</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Primaire">Primaire</option>
                  <option value="Secondaire">Secondaire</option>
                  <option value="Tertiaire">Tertiaire</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Opérationnel">Opérationnel</option>
                  <option value="Partiellement opérationnel">Partiellement opérationnel</option>
                  <option value="En maintenance">En maintenance</option>
                  <option value="Fermé">Fermé</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact et Capacité */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact et Capacité</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex: +243 999 999 999"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: contact@hgk.cd"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex: Avenue X, Quartier Y"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de lits</label>
                <input
                  type="number"
                  name="beds"
                  value={formData.beds}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Description de l'hôpital..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HospitalAddModal;
